import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  ArrowRightIcon,
  BoxesIcon,
  CarFrontIcon,
  CheckCircle2Icon,
  ClipboardListIcon,
  DownloadIcon,
  GaugeIcon,
  type LucideIcon,
  PackageCheckIcon,
  ShoppingCartIcon,
  SparklesIcon,
  TruckIcon,
} from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { CurrentVehicleNotice } from "@/components/app/current-vehicle";
import { EmptyState } from "@/components/app/empty-state";
import { Page, SectionTitle } from "@/components/app/page";
import { RoadworthinessBadge, useRoadworthinessDate } from "@/components/app/roadworthiness";
import { ReadinessChip, WorkOrderStatusBadge } from "@/components/app/status";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/misc";
import { DemoPackageImportButton } from "@/features/settings/demo-package";
import { useCurrentVehicle } from "@/lib/current-vehicle";
import { serverToday, useFormat } from "@/lib/format";
import { useGarageLanguage } from "@/lib/preferences";
import { useSystemInfo } from "@/lib/system";
import { type RouterOutputs, useTRPC } from "@/lib/trpc";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/")({
  component: OverviewPage,
});

type Overview = RouterOutputs["overview"];

function greetingKey() {
  const hour = new Date().getHours();
  if (hour < 11) return "overview.greetingMorning" as const;
  if (hour < 18) return "overview.greetingAfternoon" as const;
  return "overview.greetingEvening" as const;
}

function OverviewPage() {
  const { t } = useTranslation();
  const trpc = useTRPC();
  const format = useFormat();
  const roadworthinessDate = useRoadworthinessDate();
  const { vehicleId } = useCurrentVehicle();
  const { data, isLoading } = useQuery(trpc.overview.queryOptions({ vehicleId }));

  if (isLoading || !data) {
    return (
      <Page>
        <Skeleton className="mb-8 h-10 w-72" />
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          {Array.from({ length: 4 }, (_, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: static placeholders
            <Skeleton key={i} className="h-28 rounded-xl" />
          ))}
        </div>
      </Page>
    );
  }

  if (data.counts.parts === 0 && data.counts.vehicles === 0) return <Welcome />;

  return (
    <Page>
      <header className="mb-7">
        <h1 className="text-2xl font-semibold tracking-tight sm:text-[28px]">{t(greetingKey())}</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {format.date(new Date().toISOString())} · {t("overview.subtitle")}
        </p>
      </header>
      <CurrentVehicleNotice />

      <div className="mb-8 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatTile
          to="/to-order"
          icon={ShoppingCartIcon}
          label={t("overview.toOrderTile")}
          value={data.counts.toOrder}
          hint={t("overview.toOrderTileHint", { count: data.counts.toOrder })}
          tone={data.counts.toOrder > 0 ? "missing" : "ok"}
        />
        <StatTile
          to="/work-orders"
          icon={CheckCircle2Icon}
          label={t("overview.readyTile")}
          value={data.counts.readyWorkOrders}
          hint={t("overview.readyTileHint", { total: data.counts.openWorkOrders })}
          tone="ok"
        />
        <StatTile
          to="/orders"
          icon={TruckIcon}
          label={t("overview.arrivingTile")}
          value={data.counts.inTransit}
          hint={t("overview.arrivingTileHint", { count: data.counts.inTransit })}
          tone="transit"
        />
        <StatTile
          to="/inventory"
          search={{ status: "low" }}
          icon={GaugeIcon}
          label={t("overview.lowTile")}
          value={data.counts.lowStock}
          hint={t("overview.lowTileHint")}
          tone={data.counts.lowStock > 0 ? "partial" : "neutral"}
        />
      </div>

      <div className="grid gap-8 lg:grid-cols-[1.4fr_1fr]">
        {/* min-w-0: a grid item grows to its longest title otherwise, past the screen's edge. */}
        <section className="min-w-0">
          <SectionTitle
            action={
              <Link
                to="/work-orders"
                className="text-[13px] text-muted-foreground hover:text-foreground"
              >
                {t("overview.viewAll")}
              </Link>
            }
          >
            {t("overview.upNext")}
          </SectionTitle>
          {data.upcoming.length ? (
            <Card className="divide-y divide-border">
              {data.upcoming.map((wo) => (
                <UpcomingRow key={wo.id} wo={wo} />
              ))}
            </Card>
          ) : (
            <EmptyState compact icon={ClipboardListIcon} title={t("overview.upNextEmpty")} />
          )}
        </section>

        <div className="flex min-w-0 flex-col gap-8">
          {data.roadworthinessDue.length ? (
            <section>
              <SectionTitle>{t("roadworthiness.overviewTitle")}</SectionTitle>
              <Card className="divide-y divide-border">
                {data.roadworthinessDue.map((due) => (
                  <Link
                    key={due.vehicleId}
                    to="/vehicles/$vehicleId"
                    params={{ vehicleId: due.vehicleId }}
                    className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 px-4 py-3 text-sm transition hover:bg-muted/50"
                  >
                    <span className="min-w-0 truncate font-medium">{due.name}</span>
                    <span className="flex shrink-0 items-center gap-2 text-xs text-muted-foreground tabular">
                      {roadworthinessDate(due.dueOn)}
                      <RoadworthinessBadge due={due} />
                    </span>
                  </Link>
                ))}
              </Card>
            </section>
          ) : null}

          <section>
            <SectionTitle>{t("overview.arriving")}</SectionTitle>
            {data.arriving.length ? (
              <Card className="divide-y divide-border">
                {data.arriving.map((order) => (
                  <ArrivingRow key={order.id} order={order} />
                ))}
              </Card>
            ) : (
              <EmptyState compact icon={PackageCheckIcon} title={t("overview.arrivingEmpty")} />
            )}
          </section>

          {data.lowStock.length ? (
            <section>
              <SectionTitle>{t("overview.lowStock")}</SectionTitle>
              <Card className="divide-y divide-border">
                {data.lowStock.map((part) => (
                  <Link
                    key={part.partId}
                    to="/inventory/$partId"
                    params={{ partId: part.partId }}
                    className="flex items-center justify-between gap-3 px-4 py-3 text-sm transition hover:bg-muted/50"
                  >
                    <span className="truncate font-medium">{part.name}</span>
                    <span className="shrink-0 text-xs text-partial-fg tabular">
                      {format.qtyNumber(part.onHand, part.unit)} /{" "}
                      {format.qty(part.minStock, part.unit)}
                    </span>
                  </Link>
                ))}
              </Card>
            </section>
          ) : null}

          <section>
            <SectionTitle>{t("overview.recent")}</SectionTitle>
            {data.recentMovements.length ? (
              <Card className="divide-y divide-border">
                {data.recentMovements.map((m) => (
                  <Link
                    key={m.id}
                    to="/inventory/$partId"
                    params={{ partId: m.partId }}
                    className="flex items-center gap-3 px-4 py-2.5 text-sm transition hover:bg-muted/50"
                  >
                    <span
                      className={cn(
                        "w-16 shrink-0 text-right text-xs font-semibold tabular",
                        m.delta > 0 ? "text-ok-fg" : "text-muted-foreground",
                      )}
                    >
                      {format.delta(m.delta, m.unit)}
                    </span>
                    <span className="min-w-0 flex-1 truncate">{m.partName}</span>
                    <span className="shrink-0 text-xs text-muted-foreground">
                      {format.relativeTime(m.createdAt)}
                    </span>
                  </Link>
                ))}
              </Card>
            ) : (
              <EmptyState compact icon={BoxesIcon} title={t("overview.recentEmpty")} />
            )}
          </section>
        </div>
      </div>
    </Page>
  );
}

const TONES = {
  ok: "text-ok",
  missing: "text-missing",
  transit: "text-transit",
  partial: "text-partial",
  neutral: "text-muted-foreground",
} as const;

function StatTile({
  to,
  search,
  icon: Icon,
  label,
  value,
  hint,
  tone,
}: {
  to: string;
  search?: Record<string, string>;
  icon: LucideIcon;
  label: string;
  value: number;
  hint: string;
  tone: keyof typeof TONES;
}) {
  return (
    <Link
      to={to}
      search={search}
      className="group relative overflow-hidden rounded-xl border border-border bg-surface p-4 shadow-xs transition hover:-translate-y-0.5 hover:shadow-md focus-visible:ring-2 focus-visible:ring-ring/40 focus-visible:outline-none"
    >
      <div className="flex items-center justify-between">
        <span className="text-[13px] font-medium text-muted-foreground">{label}</span>
        <Icon className={cn("size-4", TONES[tone])} />
      </div>
      <div className="mt-3 text-3xl font-semibold tracking-tight tabular">{value}</div>
      <div className="mt-0.5 flex items-center justify-between text-xs text-muted-foreground">
        <span className="truncate">{hint}</span>
        <ArrowRightIcon className="size-3.5 opacity-0 transition group-hover:translate-x-0.5 group-hover:opacity-100" />
      </div>
    </Link>
  );
}

function UpcomingRow({ wo }: { wo: Overview["upcoming"][number] }) {
  const { t } = useTranslation();
  const format = useFormat();
  return (
    <Link
      to="/work-orders/$workOrderId"
      params={{ workOrderId: wo.id }}
      className="flex items-center gap-3 px-4 py-3 transition hover:bg-muted/50"
    >
      <div className="flex size-10 shrink-0 flex-col items-center justify-center rounded-lg bg-muted text-center leading-none">
        {wo.plannedOn ? (
          <>
            <span className="text-[10px] font-medium text-muted-foreground uppercase">
              {format.monthShort(wo.plannedOn)}
            </span>
            <span className="text-sm font-semibold tabular">
              {Number(wo.plannedOn.slice(8, 10))}
            </span>
          </>
        ) : (
          <ClipboardListIcon className="size-4 text-muted-foreground" />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium">{wo.title}</div>
        <div className="truncate text-xs text-muted-foreground">
          {wo.vehicle.name} · {t("workOrders.partsCount", { count: wo.requirementCount })}
        </div>
      </div>
      <div className="flex shrink-0 flex-col items-end gap-1">
        {wo.status === "in_progress" ? <WorkOrderStatusBadge status={wo.status} /> : null}
        {wo.availability && wo.requirementCount > 0 ? (
          <ReadinessChip readiness={wo.availability.readiness} />
        ) : null}
      </div>
    </Link>
  );
}

function ArrivingRow({ order }: { order: Overview["arriving"][number] }) {
  const { t } = useTranslation();
  const format = useFormat();
  const overdue = order.expectedOn ? order.expectedOn < serverToday() : false;
  const progress = order.lineCount ? order.receivedLineCount / order.lineCount : 0;
  return (
    <Link
      to="/orders/$orderId"
      params={{ orderId: order.id }}
      className="block px-4 py-3 transition hover:bg-muted/50"
    >
      <div className="flex items-center justify-between gap-3">
        <span className="truncate text-sm font-medium">{order.name}</span>
        <span
          className={cn(
            "shrink-0 text-xs",
            overdue ? "font-medium text-missing-fg" : "text-muted-foreground",
          )}
        >
          {order.expectedOn
            ? overdue
              ? t("orders.overdue")
              : t("orders.arrivesIn", { when: format.relativeDate(order.expectedOn) })
            : order.orderedOn
              ? t("overview.orderedOn", { date: format.shortDate(order.orderedOn) })
              : null}
        </span>
      </div>
      <div className="mt-2 flex items-center gap-3">
        <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-muted">
          <div
            className="h-full rounded-full bg-transit transition-all"
            style={{ width: `${Math.max(4, progress * 100)}%` }}
          />
        </div>
        <span className="shrink-0 text-xs text-muted-foreground tabular">
          {order.receivedLineCount}/{order.lineCount}
        </span>
      </div>
    </Link>
  );
}

function Welcome() {
  const { t } = useTranslation();
  const garageLanguage = useGarageLanguage();
  const trpc = useTRPC();
  const demo = useMutation(trpc.data.loadDemo.mutationOptions());
  const capabilities = useSystemInfo()?.capabilities;
  const steps: {
    icon: LucideIcon;
    title: string;
    body: string;
    to: string;
    search: Record<string, string>;
  }[] = [
    {
      icon: CarFrontIcon,
      title: t("overview.stepVehicle"),
      body: t("overview.stepVehicleBody"),
      to: "/vehicles",
      search: { new: "1" },
    },
    {
      icon: BoxesIcon,
      title: t("overview.stepParts"),
      body: t("overview.stepPartsBody"),
      to: "/inventory",
      search: { new: "1" },
    },
    {
      icon: ClipboardListIcon,
      title: t("overview.stepWork"),
      body: t("overview.stepWorkBody"),
      to: "/work-orders",
      search: { new: "1" },
    },
  ];
  return (
    <Page className="max-w-3xl">
      <div className="relative mt-4 overflow-hidden rounded-2xl border border-border bg-surface px-6 py-10 shadow-sm sm:px-10 sm:py-12">
        <div className="pointer-events-none absolute -top-24 -right-24 size-72 rounded-full bg-brand/15 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-32 -left-16 size-72 rounded-full bg-ok/10 blur-3xl" />
        <h1 className="relative text-3xl font-semibold tracking-tight text-balance sm:text-4xl">
          {t("overview.welcomeTitle")}
        </h1>
        <p className="relative mt-3 max-w-lg text-pretty text-muted-foreground">
          {t("overview.welcomeBody")}
        </p>
        <ol className="relative mt-8 grid gap-3 sm:grid-cols-3">
          {steps.map((step, index) => (
            <li key={step.to}>
              <Link
                to={step.to}
                search={step.search}
                className="group flex h-full flex-col gap-2 rounded-xl border border-border bg-background/60 p-4 transition hover:-translate-y-0.5 hover:border-input hover:shadow-md"
              >
                <span className="flex items-center gap-2 text-xs font-semibold text-muted-foreground">
                  <span className="flex size-5 items-center justify-center rounded-full bg-foreground text-[11px] text-background">
                    {index + 1}
                  </span>
                  <step.icon className="size-4" />
                </span>
                <span className="font-medium">{step.title}</span>
                <span className="text-sm text-muted-foreground">{step.body}</span>
              </Link>
            </li>
          ))}
        </ol>
        {capabilities?.demoGenerate ? (
          <DemoCallout body={t("overview.demoBody")}>
            <Button
              variant="outline"
              disabled={demo.isPending}
              onClick={() =>
                demo.mutate(
                  { language: garageLanguage },
                  { onSuccess: () => toast.success(t("settings.demoLoaded")) },
                )
              }
            >
              <SparklesIcon />
              {t("overview.demo")}
            </Button>
          </DemoCallout>
        ) : capabilities?.demoImport ? (
          <DemoCallout body={t("overview.demoPackageBody")}>
            <div className="flex flex-wrap gap-2">
              {capabilities.demoDownloadUrl ? (
                <Button variant="ghost" asChild>
                  <a
                    href={capabilities.demoDownloadUrl.replace("{language}", garageLanguage)}
                    download
                  >
                    <DownloadIcon />
                    {t("demoPackage.download")}
                  </a>
                </Button>
              ) : null}
              <DemoPackageImportButton />
            </div>
          </DemoCallout>
        ) : null}
      </div>
    </Page>
  );
}

function DemoCallout({ body, children }: { body: string; children: ReactNode }) {
  return (
    <div className="relative mt-8 flex flex-col gap-3 border-t border-border pt-6 sm:flex-row sm:items-center sm:justify-between">
      <p className="max-w-sm text-sm text-muted-foreground">{body}</p>
      {children}
    </div>
  );
}
